import { create } from 'zustand'

import type { Job, JobProgressData } from '@/schema'

/**
 * Queue state fed by the global SSE stream (`/api/events`) and by the
 * `/api/jobs*` queries. It is a *view* of the server, never the truth:
 * a page reload rebuilds it from `GET /api/jobs` + `GET /api/jobs/stats`.
 *
 * `jobs` keeps the last 200 jobs seen, newest first, keyed by id so a
 * `job.progress` event and a later full `Job` row merge cleanly.
 */

export const QUEUE_JOB_LIMIT = 200

/** Shape of the `queue.stats` SSE event and of `GET /api/jobs/stats` folded together. */
export interface QueueStats {
  queued: number
  running: number
  done: number
  failed: number
  spent_usd: number
  eta_s: number | null
  paused_until?: string | null
  paused_for?: 'usage_window' | 'rate_limit' | null
  interactive_queue_wait_s?: number
  oldest_queued_age_s?: number
  by_lane?: Record<string, { queued: number; running: number }>
}

/** Shape of the `job.progress` SSE event. */
export interface JobProgressEvent {
  job_id: number
  type?: string
  phase?: string | null
  pct?: number | null
  msg?: string | null
  cost_usd?: number
  status?: Job['status']
  event_seq?: number
  cancel_requested?: boolean
  data?: JobProgressData
  paused_for?: 'interactive' | 'usage_window' | 'rate_limit'
  paused_until?: string
  eta_s?: number
}

export interface AiHealthSnapshot {
  status: 'ok' | 'degraded' | 'down' | 'mock'
  last_error?: string | null
  checked_at?: string
}

export interface QueueState {
  stats: QueueStats | null
  jobs: Job[]
  aiHealth: AiHealthSnapshot | null
  /** Whether the `/api/events` stream is currently connected. */
  connected: boolean

  setStats(stats: QueueStats): void
  hydrate(jobs: Job[]): void
  upsertJob(job: Job): void
  applyProgress(event: JobProgressEvent): void
  setAiHealth(health: AiHealthSnapshot): void
  setConnected(connected: boolean): void
}

const ACTIVE: ReadonlySet<Job['status']> = new Set(['queued', 'running'])

/** HTTP responses and SSE can arrive out of order. Retries have a newer sequence. */
export function newerJob(current: Job | undefined, incoming: Job): Job {
  if (!current) return incoming
  if ((current.event_seq ?? 0) > (incoming.event_seq ?? 0)) return current
  // Compatibility with unversioned snapshots during deployment: terminal wins ties.
  if ((current.event_seq ?? 0) === (incoming.event_seq ?? 0) && !ACTIVE.has(current.status) && ACTIVE.has(incoming.status)) return current
  return incoming
}

function sortNewestFirst(jobs: Job[]): Job[] {
  return [...jobs].sort((a, b) => b.id - a.id).slice(0, QUEUE_JOB_LIMIT)
}

function mergeJob(list: Job[], job: Job): Job[] {
  const i = list.findIndex((j) => j.id === job.id)
  if (i === -1) return sortNewestFirst([job, ...list])
  const next = list.slice()
  next[i] = newerJob(list[i], job)
  return next
}

export const useQueueStore = create<QueueState>()((set, get) => ({
  stats: null,
  jobs: [],
  aiHealth: null,
  connected: false,

  setStats: (stats) => set({ stats }),
  hydrate: (jobs) => {
    // Keep progress we already know about for jobs the list also returns.
    const known = new Map(get().jobs.map((j) => [j.id, j]))
    set({ jobs: sortNewestFirst(jobs.map((j) => newerJob(known.get(j.id), j))) })
  },
  upsertJob: (job) => set((s) => ({ jobs: mergeJob(s.jobs, job) })),
  applyProgress: (ev) =>
    set((s) => {
      const i = s.jobs.findIndex((j) => j.id === ev.job_id)
      if (i === -1) return {}
      const cur = s.jobs[i]
      if (!cur) return {}
      if ((ev.event_seq ?? 0) < (cur.event_seq ?? 0)) return {}
      if ((ev.event_seq ?? 0) === (cur.event_seq ?? 0) && !ACTIVE.has(cur.status) && ev.status && ACTIVE.has(ev.status)) return {}
      const retrying = ev.status === 'queued' && !ACTIVE.has(cur.status)
      const progress = {
        phase: ev.phase !== undefined ? ev.phase : retrying ? null : cur.progress.phase,
        pct: ev.pct !== undefined ? ev.pct : retrying ? null : cur.progress.pct,
        msg: ev.msg !== undefined ? ev.msg : retrying ? null : cur.progress.msg,
        data: {
          ...(retrying ? {} : cur.progress.data),
          ...ev.data,
          ...(ev.cost_usd != null ? { cost_usd: ev.cost_usd } : {}),
          ...(ev.paused_for ? { paused_for: ev.paused_for } : {}),
          ...(ev.paused_until ? { paused_until: ev.paused_until } : {}),
          ...(ev.eta_s != null ? { eta_s: ev.eta_s } : {}),
        },
      }
      const next = s.jobs.slice()
      next[i] = {
        ...cur,
        progress,
        ...(retrying ? { error: undefined, result: undefined, finished_at: undefined, started_at: undefined } : {}),
        ...(ev.event_seq != null ? { event_seq: ev.event_seq } : {}),
        ...(ev.cancel_requested != null ? { cancel_requested: ev.cancel_requested } : {}),
        ...(ev.status ? { status: ev.status } : {}),
        ...(ev.cost_usd != null ? { cost_usd: ev.cost_usd } : {}),
      }
      return { jobs: next }
    }),
  setAiHealth: (aiHealth) => set({ aiHealth }),
  setConnected: (connected) => set({ connected }),
}))

/**
 * Jobs still queued or running, newest first. NOT a hook selector: it returns
 * a new array each call, which would make `useQueueStore(selectActiveJobs)`
 * loop under `useSyncExternalStore`. Select `s.jobs` and `useMemo` this.
 */
export const selectActiveJobs = (s: Pick<QueueState, 'jobs'>): Job[] => s.jobs.filter((j) => ACTIVE.has(j.status))

/** Selector (returns a primitive, safe as a hook selector): any job queued or running. */
export const selectBusy = (s: QueueState): boolean => (s.stats ? s.stats.queued + s.stats.running > 0 : s.jobs.some((j) => ACTIVE.has(j.status)))
